import React, { useCallback, useEffect, useState } from 'react';

import { Navigate, Link as ReactRouterLink, Route, Routes, useParams } from 'react-router-dom';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Thumbnail from '@amzn/meridian/thumbnail';
import Breadcrumbs from 'src/components/shared/breadcrumbs/breadcrumbs';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { BADGE_PHOTO_THUMBNAIL_URL } from 'src/config/ExternalLinks';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type { AssociateProfile } from 'src/services/associateProfile/types';
import TaskService from 'src/services/tasks/taskService';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import type { DataFetchingState } from 'src/types';
import {
  regionTaskListToRestrictionItemTasks,
  type RestrictionListParentTask,
  type RestrictionListTask,
} from 'src/utils/restrictionUtils';

import './associateProfile.scss';
import AssociateProfileEmployeeInfo from './associateProfileEmployeeInfo';
import EmDash from './emdash';
import ModifiedDuty from './modifiedDuty';

import AssociateProfileHistory from './history/associateProfileHistory';
import AssociateProfilePlacements from './placements/associateProfilePlacements';
import AssociateProfileRestrictions from './restrictions/associateProfileRestrictions';

type Tab = 'placements' | 'restrictions' | 'history';

type PageState = DataFetchingState<{
  associateProfile: AssociateProfile;
  regionTasks: (RestrictionListTask | RestrictionListParentTask)[];
}>;

const DEFAULT_PAGE_STATE: PageState = {
  status: 'loading',
} as const;

const AssociatesProfile = () => {
  const { alias, '*': restOfPath } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const dispatch = useAppDispatch();
  const [pageState, setPageState] = useState<PageState>(DEFAULT_PAGE_STATE);

  // Get current tab from URL
  const currentTab = restOfPath?.split('/')[0] || 'placements';

  const openNewJmrInNewTab = useCallback(() => {
    if (pageState.status !== 'success') {
      return;
    }

    window.open(
      `${window.location.origin}/newJmr/${pageState.data.associateProfile.alias}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, [pageState]);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchPageData = async () => {
      if (!alias || selectedSite.id === 0) {
        return;
      }

      setPageState({ status: 'loading' });

      const regionId = selectedSite?.regionId ?? -1;

      try {
        const [{ data: associateProfile }, rawRegionTasks] = await Promise.all([
          associateProfileService.getAssociateProfile(alias, selectedSite.id, { signal }),
          regionId > -1 ? TaskService.fetchRegionTasksByRegionId(regionId, { signal }) : Promise.resolve([]),
        ]);

        if (!signal.aborted) {
          setPageState({
            status: 'success',
            data: { associateProfile, regionTasks: regionTaskListToRestrictionItemTasks(rawRegionTasks) },
          });
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error('Failed to fetch page data:', error);
          setPageState({ status: 'error' });
          dispatch(
            createToast({ type: 'error', message: 'There was an issue fetching page data', timeout: TOAST_TIMEOUT }),
          );
        }
      }
    };

    fetchPageData();
    return () => abortController.abort();
  }, [alias, selectedSite, dispatch]);

  if (pageState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (pageState.status === 'error') {
    return <Text>Oops! Something went wrong.</Text>;
  }

  const { associateProfile, regionTasks } = pageState.data;

  return (
    <div className="associate-profile">
      <Breadcrumbs
        breadcrumbList={[
          { url: '/placementsOverview', label: 'Placements Overview' },
          { url: `/associateProfile/${associateProfile?.alias}`, label: `Associate ${associateProfile?.name}` },
        ]}
      />
      <div className="associate-profile__header">
        <Row alignmentVertical="center">
          <Thumbnail source={BADGE_PHOTO_THUMBNAIL_URL + associateProfile?.alias} />
          <Box>
            <Text type="h400">{associateProfile?.name ?? <EmDash />}</Text>
            <Text type="b300">
              {associateProfile?.jobTitle ?? <EmDash />} &bull; &nbsp;
              {associateProfile?.alias ? (
                <Link
                  type="secondary"
                  href={`https://fclm-portal.amazon.com/search?term=${associateProfile?.alias}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >{`${associateProfile?.alias}@`}</Link>
              ) : (
                <EmDash />
              )}
            </Text>
          </Box>
          <Button className="create-placement-button" onClick={openNewJmrInNewTab}>
            Create Placement
          </Button>
        </Row>
        <Row className="associate-profile__info" spacing="500">
          <AssociateProfileEmployeeInfo label="Employee ID" value={associateProfile?.employeeId} />
          <AssociateProfileEmployeeInfo
            label="Manager"
            value={associateProfile?.managersName}
            url={`https://phonetool.amazon.com/users/${associateProfile?.managersAlias}`}
          />
          <AssociateProfileEmployeeInfo label="Home Location" value={associateProfile?.location} />
          <AssociateProfileEmployeeInfo label="Shift" value={associateProfile?.shift} />
          {selectedSite.countryId === COUNTRY_ID_MAPPING.USA_COUNTRY_ID && (
            <ModifiedDuty value={associateProfile?.totalAccommodatedDays} />
          )}
        </Row>
      </div>
      <div className="associate-profile__content">
        <TabGroup value={currentTab} onChange={() => {}}>
          <ReactRouterLink to="placements">
            <Tab value="placements" aria-controls="tabpanel-placements" id="tab-placements">
              Placements
            </Tab>
          </ReactRouterLink>
          <ReactRouterLink to="restrictions">
            <Tab value="restrictions" aria-controls="tabpanel-restrictions" id="tab-restrictions">
              Restrictions
            </Tab>
          </ReactRouterLink>
          <ReactRouterLink to="history">
            <Tab value="history" aria-controls="tabpanel-history" id="tab-history">
              History
            </Tab>
          </ReactRouterLink>
        </TabGroup>
        <Routes>
          <Route index element={<Navigate to="placements" replace />} />
          <Route path="placements" element={<AssociateProfilePlacements regionTasks={regionTasks} />} />
          <Route path="restrictions" element={<AssociateProfileRestrictions />} />
          <Route path="history" element={<AssociateProfileHistory regionTasks={regionTasks} />} />
          <Route path="*" element={<Navigate to="placements" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default AssociatesProfile;
